import { Alert, Button, Checkbox, InputField } from '@design-system';
import { AddressElement } from '@stripe/react-stripe-js';
import { customsCodeIsRequired } from '@utils/address';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { COUNTRY_CODES } from '../../../../constants/locations';
import { useFlow } from '../../../../providers';
import { useCreateAddressMutation } from '../../../../types/generated';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';

const allowedCountries = COUNTRY_CODES.map((c) => c);

interface StripeAddress {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country: string;
  };
  phone?: string | null;
}

interface Props {
  onAddressUpdate: (addressId: string) => Promise<void>;
}

interface FormValues {
  customsCode: string | null;
  isDefaultAddress: boolean;
}

export const AddShippingAddress = ({ onAddressUpdate }: Props) => {
  const { t } = useTranslation(['product', 'countries']);
  const { currentStepId } = useFlow();
  const [stripeDetails, setStripeDetails] = useState<StripeAddress>({});
  const [addressReady, setAddressReady] = useState(false);
  const [emitter] = useAnalyticsEmitter();

  const isCustomsCodeRequired = customsCodeIsRequired(
    stripeDetails.address?.country,
  );

  const form = useForm<FormValues>({
    defaultValues: {
      customsCode: null,
      isDefaultAddress: true,
    },
  });

  const [createAddressMutation, { error, loading }] =
    useCreateAddressMutation();

  async function handleAddressCreation(input: FormValues) {
    const address = stripeDetails.address!;
    const [firstName, lastName] = (stripeDetails.name ?? '').split(
      /[ ]{1}(.*)/,
    );

    const response = await createAddressMutation({
      variables: {
        address: {
          isDefault: input.isDefaultAddress,
          addressLine1: address.line1 || void 0,
          addressLine2: address.line2 || void 0,
          city: address.city || void 0,
          countryName: `${t(`countries:${address.country}`)}`,
          countryCode: address.country,
          customsCode: input.customsCode,
          firstName,
          lastName,
          phone: stripeDetails.phone ?? void 0,
          provinceName: address.state ?? void 0,
          zip: address.postal_code ?? void 0,
        },
      },
    });

    if (response.errors) {
      return;
    }

    if (response.data?.createAddress.id) {
      emitter('new details', 'saved');
      onAddressUpdate(response.data?.createAddress.id);
    }
  }

  return (
    <form
      className="flex flex-col"
      onSubmit={(ev) => void form.handleSubmit(handleAddressCreation)(ev)}
    >
      {error && (
        <div className="mt-8">
          <Alert status="error">
            {t('product:drawers.shipping.failureMessageGeneric')}
          </Alert>
        </div>
      )}
      {currentStepId === 'add_shipping_address' && (
        <div>
          <AddressElement
            onChange={(event) => {
              setStripeDetails(event.value);
              if (event.complete) {
                setAddressReady(true);
              } else if (addressReady) {
                setAddressReady(false);
              }
            }}
            options={{
              allowedCountries,
              mode: 'shipping',
              autocomplete: {
                mode: 'automatic',
              },
              display: {
                name: 'full',
              },
              fields: {
                phone: 'always',
              },
              validation: {
                phone: {
                  required: 'always',
                },
              },
            }}
          />
        </div>
      )}
      {stripeDetails?.address?.country && isCustomsCodeRequired && (
        <div className="mt-3">
          <InputField
            {...form.register('customsCode', {
              required: {
                message: t('drawers.shipping.customs.error'),
                value: isCustomsCodeRequired,
              },
            })}
            error={form.formState.errors.customsCode?.message}
            id="customsCode"
            label={t(
              `drawers.shipping.customs.labels.${stripeDetails.address.country}`,
            )}
            name="customsCode"
          />
          <p className="mt-2 text-xs text-neutral-4">
            {t('drawers.shipping.customs.text', {
              country: `${t(`countries:${stripeDetails.address.country}`)}`,
              label: t(
                `drawers.shipping.customs.labels.${stripeDetails.address.country}`,
              ),
            })}
          </p>
        </div>
      )}
      <div className="my-8">
        <Checkbox
          checked={Boolean(form.watch('isDefaultAddress'))}
          id="isDefaultAddress"
          label={t('drawers.shipping.defaultButton')}
          name="isDefaultAddress"
          onChange={(e) =>
            form.setValue('isDefaultAddress', e.currentTarget.checked)
          }
          type="checkbox"
          value="isDefaultAddress"
        />
      </div>
      <Button
        disabled={loading || !addressReady}
        fullWidth
        label={t('drawers.shipping.saveButton')}
        type="submit"
        variant="primary"
      />
    </form>
  );
};
